import { createContext, useContext, useLayoutEffect, useState, type ReactNode } from "react";
import { Outlet } from "react-router";
import { HomeBottomNav } from "./HomeBottomNav";

const ActionPillContext = createContext<(pill: ReactNode) => void>(() => undefined);

/**
 * Shows `pill` in the persistent nav for the calling page's mount lifetime;
 * registration and cleanup are owned here. Pass a memoized element: an
 * unmemoized pill re-registers every render and loops through the layout
 * re-render (a loud dev error, by design).
 */
export function useHomeNavActionPill(pill: ReactNode): void {
  const setActionPill = useContext(ActionPillContext);
  useLayoutEffect(() => {
    setActionPill(pill);
    return () => setActionPill(null);
  }, [setActionPill, pill]);
}

/**
 * Layout route that keeps one `HomeBottomNav` mounted across the home-level
 * pages, so tab changes animate in place (icon crossfade, indicator slide)
 * instead of remounting already-final.
 */
export function HomeNavLayout() {
  const [actionPill, setActionPill] = useState<ReactNode>(null);

  return (
    <ActionPillContext.Provider value={setActionPill}>
      <Outlet />
      <HomeBottomNav actionPill={actionPill} />
    </ActionPillContext.Provider>
  );
}
